import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { useFileTransfer } from './hooks/useFileTransfer';
import { Header } from './components/Header';
import { AuthModal } from './components/AuthModal';
import { FileHistory } from './components/FileHistory';
import { RadarView } from './components/RadarView';
import { FileTransferPanel } from './components/FileTransferPanel';
import { SpaceLinkBanner } from './components/SpaceLinkBanner';
import { PeerNameDisplay } from './components/PeerNameDisplay';
import { WelcomeModal } from './components/WelcomeModal';
import { PaymentVerificationModal } from './components/PaymentVerificationModal';
import { PremiumSuccessModal } from './components/PremiumSuccessModal';
import { EmailConfirmationModal } from './components/EmailConfirmationModal';
import { FileReceivedModal } from './components/FileReceivedModal';
import { SettingsModal } from './components/SettingsModal';
import { PeerActionMenu } from './components/PeerActionMenu';
import { MessageComposer } from './components/MessageComposer';
import { MessageReceivedModal } from './components/MessageReceivedModal';
import { subscriptionService } from './services/subscription';
import { generateUniqueSpaceId, getOrCreateSpace, getUserId } from './services/spaces';
import type { PeerData } from './types';


/**
 * Get space ID from URL path or create new one
 * Returns [spaceId, isCreator] or null if generation fails
 */
async function getOrCreateSpaceId(): Promise<[string, boolean] | null> {
  // Check if we're on /:id route
  const pathMatch = window.location.pathname.match(/^\/([^\/]+)$/);
  
  if (pathMatch) {
    const spaceId = pathMatch[1];
    console.log('📍 Found existing space ID in URL:', spaceId);
    return [spaceId, false];
  }
  
  // Generate new unique space ID
  const newSpaceId = await generateUniqueSpaceId();
  
  if (!newSpaceId) {
    console.error('❌ Failed to generate unique space ID');
    return null;
  }
  
  // Navigate to /:id
  const newUrl = `/${newSpaceId}`;
  window.history.replaceState({}, '', newUrl);
  
  console.log('🆕 Created new space ID:', newSpaceId);
  return [newSpaceId, true];
}

/**
 * Main application component
 */
function App() {
  const { user, loading, error, signIn, signUp, signInWithGoogle, signOut, getUserTier, isAuthenticated } =
    useAuth();
  const userTier = getUserTier();
  const [spaceInfo, setSpaceInfo] = useState<[string, boolean] | null>(null);
  const spaceId = spaceInfo?.[0] || '';
  const isSpaceCreator = spaceInfo?.[1] || false;
  const fileTransfer = useFileTransfer(userTier, user?.id, spaceId, user?.email);

  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalMode, setAuthModalMode] = useState<'signin' | 'signup'>('signin');
  const [showHistory, setShowHistory] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedPeer, setSelectedPeer] = useState<PeerData | null>(null);
  const [spaceError, setSpaceError] = useState<string | null>(null);
  const [spaceLoading, setSpaceLoading] = useState(true);
  const [showWelcomeModal, setShowWelcomeModal] = useState(() => {
    return !localStorage.getItem('welcomeModalSeen');
  });
  const [showPaymentVerification, setShowPaymentVerification] = useState(false);
  const [showPremiumSuccess, setShowPremiumSuccess] = useState(false);
  const [showEmailConfirmation, setShowEmailConfirmation] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [isConnectingToLocalPeer, setIsConnectingToLocalPeer] = useState(false);
  const [showActionMenu, setShowActionMenu] = useState(false);
  const [actionMenuPeer, setActionMenuPeer] = useState<PeerData | null>(null);
  const [showMessageComposer, setShowMessageComposer] = useState(false);
  const [receivedMessage, setReceivedMessage] = useState<{ content: string; from: string } | null>(null);
  
  // Initialize space ID from URL or generate new one
  useEffect(() => {
    async function initSpaceId() {
      const result = await getOrCreateSpaceId();
      if (result) {
        setSpaceInfo(result);
      } else {
        setSpaceError('Failed to generate space ID. Please refresh the page.');
        setSpaceLoading(false);
      }
    }
    
    initSpaceId();
  }, []);
  
  // Check for payment_pending query param AFTER user data is loaded
  useEffect(() => {
    // Don't check until loading is complete
    if (loading) return;
    
    const urlParams = new URLSearchParams(window.location.search);
    const paymentPending = urlParams.get('payment_pending');
    
    if (paymentPending === 'true' && user) {
      // Remove query param from URL
      window.history.replaceState({}, '', window.location.pathname);
      
      if (userTier === 'subscribed') {
        // User is already premium, show success message
        console.log('✅ User is already premium, showing success message');
        setShowPremiumSuccess(true);
      } else {
        // User is not premium yet, show verification modal
        console.log('🔍 Payment pending detected, showing verification modal');
        setShowPaymentVerification(true);
      }
    } else if (paymentPending === 'true') {
      console.log('🔍 Payment pending query param found but user not authenticated');
      // Remove query param anyway to clean URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [loading, user, userTier]);
  
  // Check for confirmed query param AFTER user data is loaded
  useEffect(() => {
    // Don't check until loading is complete
    if (loading) return;
    
    const urlParams = new URLSearchParams(window.location.search);
    const confirmed = urlParams.get('confirmed');
    
    if (confirmed === 'true') {
      if (user) {
        // User is authenticated, show success modal
        console.log('✅ Email confirmed, user authenticated, showing confirmation modal');
        console.log('✅ User email:', user.email);
        
        // Remove query param from URL
        window.history.replaceState({}, '', window.location.pathname);
        
        // Show email confirmation success modal
        setShowEmailConfirmation(true);
      } else {
        // User not yet authenticated, wait for auth to complete
        // Don't remove the parameter yet - it will be checked again when user loads
        console.log('🔍 Confirmed query param found, waiting for user authentication...');
      }
    }
  }, [loading, user]);
  
  // Initialize space and join signaling
  useEffect(() => {
    // Wait for spaceId to be initialized
    if (!spaceId) return;
    
    async function initSpace() {
      setSpaceLoading(true);
      setSpaceError(null);
      
      try {
        console.log('🚀 Initializing space:', spaceId);
        
        // Get user ID (authenticated or anonymous)
        const userId = getUserId(user?.id);
        
        // Create or validate space in Supabase
        const success = await getOrCreateSpace(spaceId, userId);
        
        if (!success) {
          setSpaceError('Failed to create or join space. Please try again.');
          setSpaceLoading(false);
          return;
        }
        
        // Join the signaling channel
        await fileTransfer.joinSpace(spaceId);
        setSpaceLoading(false);
        
      } catch (err) {
        console.error('Error initializing space:', err);
        setSpaceError('Failed to initialize space. Please refresh the page.');
        setSpaceLoading(false);
      }
    }
    
    initSpace();
    
    return () => {
      console.log('🧹 Cleaning up space');
      fileTransfer.cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spaceId, user?.id]);

  // Set up message received callback
  useEffect(() => {
    fileTransfer.setOnMessageReceived((htmlContent, from) => {
      console.log('📨 Message received from:', from);
      setReceivedMessage({ content: htmlContent, from });
    });
  }, [fileTransfer]);

  // Reset connecting state when connection completes or disconnects
  useEffect(() => {
    if (fileTransfer.isConnected || fileTransfer.error) {
      setIsConnectingToLocalPeer(false);
    }
  }, [fileTransfer.isConnected, fileTransfer.error]);

  if (loading || spaceLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-b-4 border-blue-500"></div>
          <p className="mt-6 text-gray-300 text-lg">
            {loading ? 'Loading...' : 'Setting up space...'}
          </p>
        </div>
      </div>
    );
  }

  if (spaceError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <div className="max-w-md text-center">
          <div className="text-6xl mb-4">❌</div>
          <h2 className="text-2xl font-bold text-white mb-2">Space Error</h2>
          <p className="text-gray-400 mb-6">{spaceError}</p>
          <button
            onClick={() => window.location.href = '/'}
            className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg font-medium transition-all shadow-lg"
          >
            Create New Space
          </button>
        </div>
      </div>
    );
  }

  const handlePeerClick = (peer: PeerData) => {
    console.log('🖱️ Peer clicked:', peer);
    
    // Show action menu
    setActionMenuPeer(peer);
    setShowActionMenu(true);
  };

  const handleSendFileAction = async (peer: PeerData) => {
    setShowActionMenu(false);
    
    // Open file picker
    const input = document.createElement('input');
    input.type = 'file';
    input.onchange = async (e: Event) => {
      const target = e.target as HTMLInputElement;
      const file = target.files?.[0];
      if (file) {
        console.log('📄 File selected:', file.name, file.size);
        setSelectedFile(file);
        setSelectedPeer(peer);
        console.log('✅ State updated - selectedPeer:', peer, 'selectedFile:', file.name);
        
        // If clicking a local peer, initiate connection after file selection
        if (peer.discoveryMethod === 'local') {
          console.log('🔗 Initiating local peer connection...');
          setIsConnectingToLocalPeer(true);
          await fileTransfer.connectToLocalPeer(peer);
        }
      } else {
        console.log('❌ No file selected');
      }
    };
    input.click();
  };

  const handleSendMessageAction = async (peer: PeerData) => {
    setShowActionMenu(false);
    setSelectedPeer(peer);
    
    // If clicking a local peer, initiate connection first
    if (peer.discoveryMethod === 'local') {
      console.log('🔗 Initiating local peer connection for message...');
      setIsConnectingToLocalPeer(true);
      await fileTransfer.connectToLocalPeer(peer);
    }
    
    // Open message composer
    setShowMessageComposer(true);
  };

  const handleSendMessage = async (htmlContent: string) => {
    if (!fileTransfer.isConnected) {
      console.error('❌ Cannot send message: not connected');
      return;
    }

    try {
      await fileTransfer.sendMessage(htmlContent);
      console.log('✅ Message sent successfully');
      setShowMessageComposer(false);
      setSelectedPeer(null);
    } catch (error) {
      console.error('❌ Failed to send message:', error);
      alert('Failed to send message. Please try again.');
    }
  };

  const handleToggleLocalDiscovery = async () => {
    if (fileTransfer.isLocalDiscoveryEnabled) {
      await fileTransfer.disableLocalDiscovery();
    } else {
      await fileTransfer.enableLocalDiscovery();
    }
  };

  const handleSendFile = async () => {
    if (!selectedFile || !fileTransfer.isConnected) return;
    
    const success = await fileTransfer.sendFile(selectedFile);
    if (success) {
      setSelectedFile(null);
      setSelectedPeer(null);
    }
  };

  // Determine connection state for radar view
  const getConnectionState = (): 'idle' | 'waiting' | 'connecting' | 'connected' => {
    if (fileTransfer.isConnected) return 'connected';
    if (isConnectingToLocalPeer) return 'connecting';
    if (fileTransfer.peerCount >= 2) return 'connecting';
    if (fileTransfer.peerCount > 0) return 'waiting';
    return 'waiting';
  };

  const handleUpgrade = async () => {
    // If user is not signed in, close transfer panel and open auth modal instead
    if (!user?.email) {
      setSelectedPeer(null); // Close FileTransferPanel
      setSelectedFile(null);
      setAuthModalMode('signup'); // Show signup form for upgrade
      setShowAuthModal(true);
      return;
    }
    
    try {
      await subscriptionService.openCheckout(user.email, spaceId);
    } catch (error) {
      console.error('Failed to open checkout:', error);
      alert('Unable to open checkout. Please check your Lemon Squeezy configuration.');
    }
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      <Header
        isAuthenticated={isAuthenticated}
        userTier={userTier}
        userEmail={user?.email}
        onSignIn={() => {
          setAuthModalMode('signin');
          setShowAuthModal(true);
        }}
        onSignOut={signOut}
        onUpgrade={userTier !== 'subscribed' ? handleUpgrade : undefined}
        onViewHistory={isAuthenticated ? () => setShowHistory(true) : undefined}
      />

      <main className="flex-1 flex flex-col items-center justify-center px-4 py-4 overflow-hidden" role="main" aria-label="File sharing interface">
        {/* Peer Name Display */}
        <div className="w-full max-w-3xl mb-2 flex justify-center">
          <PeerNameDisplay 
            name={fileTransfer.localPeerName}
            onNameChange={fileTransfer.updatePeerName}
          />
        </div>

        {/* Main Radar View */}
        <div className="flex-1 flex items-center justify-center w-full max-w-5xl min-h-0">
          <RadarView
            peerCount={fileTransfer.peerCount}
            isConnected={fileTransfer.isConnected}
            remotePeer={fileTransfer.remotePeer}
            onPeerClick={handlePeerClick}
            connectionState={getConnectionState()}
            spaceUrl={window.location.href}
            isLocalDiscoveryEnabled={fileTransfer.isLocalDiscoveryEnabled}
            localPeers={fileTransfer.localPeers}
            onOpenSettings={() => setShowSettings(true)}
          />
          {/* Debug info - show with ?debug=true */}
          {new URLSearchParams(window.location.search).get('debug') === 'true' && (
            <div className="fixed bottom-4 left-4 bg-black/80 text-white text-xs p-3 rounded font-mono space-y-1">
              <div>isConnected: {String(fileTransfer.isConnected)}</div>
              <div>peerCount: {fileTransfer.peerCount}</div>
              <div>remotePeer: {fileTransfer.remotePeer ? fileTransfer.remotePeer.name : 'null'}</div>
              <div>selectedPeer: {selectedPeer ? selectedPeer.name : 'null'}</div>
              <div>selectedFile: {selectedFile ? selectedFile.name : 'null'}</div>
              <div>modalOpen: {String(!!selectedPeer)}</div>
            </div>
          )}
        </div>

        {/* Error Display */}
        {fileTransfer.error && (
          <div className="w-full max-w-3xl mb-2">
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 text-red-300 text-sm backdrop-blur-sm">
              {fileTransfer.error}
            </div>
          </div>
        )}

        {/* Footer Info */}
        <footer className="pb-4 pt-2 text-center space-y-2 flex-shrink-0" role="contentinfo">
          <p className="text-xs text-gray-500">
            Direct P2P • End-to-End Encrypted • No Cloud Storage
          </p>
          <nav className="flex items-center justify-center gap-4 text-xs" aria-label="Footer navigation">
            <Link 
              to="/privacy" 
              className="text-gray-500 hover:text-gray-300 transition-colors"
            >
              Privacy Policy
            </Link>
            <span className="text-gray-600" aria-hidden="true">•</span>
            <Link 
              to="/terms" 
              className="text-gray-500 hover:text-gray-300 transition-colors"
            >
              Terms & Conditions
            </Link>
          </nav>
        </footer>
      </main>

      {/* Floating Space Link Banner */}
      <SpaceLinkBanner spaceUrl={window.location.href} />

      {/* File Transfer Panel */}
      <FileTransferPanel
        isOpen={!!selectedPeer && !!selectedFile}
        selectedPeer={selectedPeer}
        selectedFile={selectedFile}
        transfers={fileTransfer.transfers}
        userTier={userTier}
        onSendFile={handleSendFile}
        onClose={() => {
          setSelectedPeer(null);
          setSelectedFile(null);
        }}
        onUpgrade={userTier !== 'subscribed' ? handleUpgrade : undefined}
      />

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSignIn={signIn}
        onSignUp={signUp}
        onSignInWithGoogle={signInWithGoogle}
        error={error}
        initialMode={authModalMode}
      />

      {isAuthenticated && user && (
        <FileHistory
          userId={user.id}
          isOpen={showHistory}
          onClose={() => setShowHistory(false)}
        />
      )}

      <WelcomeModal
        isOpen={showWelcomeModal}
        onClose={() => setShowWelcomeModal(false)}
        spaceUrl={window.location.href}
        isSpaceCreator={isSpaceCreator}
      />

      <PaymentVerificationModal
        isOpen={showPaymentVerification}
        onSuccess={() => {
          setShowPaymentVerification(false);
          // Reload the page to refresh user profile
          window.location.reload();
        }}
        onTimeout={() => {
          setShowPaymentVerification(false);
          alert('Payment verification timed out. Please refresh the page or contact support if the issue persists.');
        }}
      />

      <PremiumSuccessModal
        isOpen={showPremiumSuccess}
        onClose={() => setShowPremiumSuccess(false)}
      />

      <EmailConfirmationModal
        isOpen={showEmailConfirmation}
        onClose={() => setShowEmailConfirmation(false)}
      />

      <FileReceivedModal
        isOpen={!!fileTransfer.receivedFile}
        fileName={fileTransfer.receivedFile?.file.name || ''}
        fileSize={fileTransfer.receivedFile?.file.size || 0}
        onDownload={fileTransfer.downloadReceivedFile}
        onClose={fileTransfer.clearReceivedFile}
      />

      <SettingsModal
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        isLocalDiscoveryEnabled={fileTransfer.isLocalDiscoveryEnabled}
        onToggleLocalDiscovery={handleToggleLocalDiscovery}
      />

      <PeerActionMenu
        isOpen={showActionMenu}
        peer={actionMenuPeer}
        onSendFile={() => actionMenuPeer && handleSendFileAction(actionMenuPeer)}
        onSendMessage={() => actionMenuPeer && handleSendMessageAction(actionMenuPeer)}
        onClose={() => {
          setShowActionMenu(false);
          setActionMenuPeer(null);
        }}
      />

      <MessageComposer
        isOpen={showMessageComposer}
        peer={selectedPeer}
        onSend={handleSendMessage}
        onClose={() => {
          setShowMessageComposer(false);
          setSelectedPeer(null);
        }}
      />

      <MessageReceivedModal
        isOpen={!!receivedMessage}
        htmlContent={receivedMessage?.content || ''}
        from={receivedMessage?.from || ''}
        onClose={() => setReceivedMessage(null)}
      />
    </div>
  );
}

export default App;
